import { hasNonMagicAction } from '../../../shared/rules/fighter.mjs';
import { loadingShotSpent, weaponHasProperty } from '../../../shared/rules/weaponRange.mjs';
export const getWeaponInstanceId = (weapon = {}, slot = '') => String(
  weapon.inventoryId ?? weapon.instanceId ?? weapon._id ?? weapon.id ?? `${slot}:${weapon.index ?? weapon.name ?? 'weapon'}`
);

const hasLightProperty = (weapon) => (Array.isArray(weapon?.properties) ? weapon.properties : [])
  .some((property) => String(property?.name || property).trim().toLowerCase().split(/\s|\(/)[0] === 'light');

export const getLightExtraAttackEligibility = ({ combat, combatantId, attack, character } = {}) => {
  const ledger = combat?.turnActionLedger;
  if (!ledger || ledger.combatantId !== String(combatantId) || ledger.turnSequence !== combat?.turnSequence) {
    return { eligible: false, mode: null, reasonCode: 'not-current-turn' };
  }
  if (!ledger.light?.available || !ledger.light.triggeringWeaponInstanceId) return { eligible: false, mode: null, reasonCode: 'no-light-trigger' };
  if (ledger.light.spent) return { eligible: false, mode: null, reasonCode: 'light-already-spent' };
  if (![ledger.attackAction, ...(ledger.completedAttackActions || [])].some(a => a?.id === ledger.light.attackActionId)) return { eligible: false, mode: null, reasonCode: 'wrong-attack-action' };
  if (!attack?.weapon || getWeaponInstanceId(attack.weapon, attack.slot) === ledger.light.triggeringWeaponInstanceId) {
    return { eligible: false, mode: null, reasonCode: 'same-weapon-instance' };
  }
  if (!hasLightProperty(attack.weapon)) return { eligible: false, mode: null, reasonCode: 'candidate-not-light' };
  const nick = String(attack.mastery?.label || attack.weapon?.mastery || '').toLowerCase() === 'nick'
    && attack.hasWeaponMastery === true;
  if (!nick && ledger.bonusActionSpent) return { eligible: false, mode: null, reasonCode: 'bonus-action-spent' };
  return { eligible: true, mode: nick ? 'nick' : 'bonus-action', reasonCode: null, attackActionId: ledger.light.attackActionId };
};

export const getWeaponAttackModeAvailability = ({ combat, combatantId, attack, character } = {}) => {
  const ledger = combat?.turnActionLedger;
  const hasCurrentLedger = Boolean(
    ledger
    && ledger.combatantId === String(combatantId)
    && ledger.turnSequence === combat?.turnSequence
  );
  const allowed = Number(ledger?.attackAction?.ordinaryAttacksAllowed);
  const spent = Number(ledger?.attackAction?.ordinaryAttacksSpent);
  // Loading: a weapon fired in the current Attack action can't fire again in it, whatever attacks remain.
  const loadingSpent = hasCurrentLedger && weaponHasProperty(attack?.weapon, 'loading') && Boolean(ledger.attackAction?.id)
    && Number.isFinite(allowed) && Number.isFinite(spent) && spent < allowed && loadingShotSpent(ledger.attackAction, getWeaponInstanceId(attack.weapon, attack.slot));
  const ordinaryAvailable = !hasCurrentLedger || (!loadingSpent && (hasNonMagicAction(ledger) || (Boolean(ledger.attackAction?.id) && Number.isFinite(allowed) && Number.isFinite(spent) && spent < allowed)));
  const lightExtra = getLightExtraAttackEligibility({ combat, combatantId, attack, character });

  return {
    ordinaryAvailable,
    lightExtra: lightExtra.eligible ? lightExtra : null,
    hasAnyMode: ordinaryAvailable || lightExtra.eligible,
  };
};

export const abilityDamageModifier = (modifier, { policy = 'normal', allowPositiveOverride = false } = {}) => {
  const value = Number(modifier) || 0;
  return policy === 'omit-positive' && !allowPositiveOverride ? Math.min(0, value) : value;
};

export const lightDamageAbilityModifier = (modifier, { twoWeaponFighting = false } = {}) => abilityDamageModifier(modifier, {
  policy: 'omit-positive', allowPositiveOverride: twoWeaponFighting,
});

